/** Pure rules for selling a Family Plan from the Order Request modal (CWS-1580). */
import type { FamilyPlanSettings } from '../../../shared/types/family-plan-settings.types';
import type { CreateOrderAddOnVariantPayload } from '../../../services/order-creation.service';
import type {
  FamilyMemberDraft,
  FamilySaleMember,
  FamilySaleQuote,
  FamilySaleQuotePayload,
  FamilySalePrimary,
} from '../../../shared/types/family-sale.types';
import type { OrderRequestExpandedPackage } from '../models/notification-order-request.models';
import {
  sanitizeCustomerPhoneInput,
  validateCustomerName,
  validateCustomerPhone,
} from '../../../shared/validators/customer-contact-field.rules';

const MONTHLY_WASH_TYPE = 'monthly_membership';
const MAX_PLATE_LENGTH = 20;

const toCents = (dollars: number): number => Math.round(dollars * 100);
const toDollars = (cents: number): number => cents / 100;

export interface FamilyMemberErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  plate?: string;
  packageId?: string;
  discount?: string;
}

interface FamilyErrorDetail {
  plate_key?: string;
  package_name?: string | null;
}

const ERROR_MESSAGES: Record<string, string> = {
  FAMILY_MAX_VEHICLES_EXCEEDED: 'This family is already at the maximum number of vehicles.',
  FAMILY_DISCOUNT_REQUIRED: 'Pick a discount for every added vehicle.',
  FAMILY_DISCOUNT_NOT_ALLOWED: 'That discount is not available. Pick one from the list.',
  FAMILY_CUSTOM_DISCOUNT_DISABLED: 'Custom discounts are not available for Family Plan.',
  FAMILY_DISCOUNT_EXCEEDS_PRICE: 'The discount cannot be larger than the package price.',
  FAMILY_DUPLICATE_PLATE: 'The same license plate is added more than once.',
  FAMILY_SETTINGS_MISSING:
    'Family Plan is not set up for this location. Ask an admin to configure it.',
  FAMILY_PACKAGE_NOT_ELIGIBLE: 'One of the packages cannot be sold as a Family Plan.',
  FAMILY_ADDON_INVALID: 'One of the add-ons is not available for this package.',
  FAMILY_PRIMARY_PHONE_REQUIRED: 'The primary customer needs a phone number.',
  FAMILY_INVALID_FIRST_TIME_DISCOUNT:
    'The first-time discount is not valid. Remove it and try again.',
  FAMILY_DISCOUNT_NOT_FIRST_TIME: 'That discount is not a first-time purchase discount.',
  FAMILY_INVALID_PRICE: 'One of the packages has no valid price. Check the package setup.',
  FAMILY_INVALID_COMPOSITION: 'The family needs a primary and at least one added vehicle.',
  LPR_ENTRY_NOT_FOUND: 'This scan is no longer available. Close the form and scan the car again.',
  LPR_ENTRY_MISMATCH: "This scan is for a different vehicle than the primary's.",
  LPR_ENTRY_ALREADY_LINKED:
    'This scan already has an order. Close the form and scan the car again.',
  FAMILY_PAYMENT_AMOUNT_MISMATCH:
    'The amount charged did not match the sale, so the payment was refunded in full. Try again.',
  PLATE_ALREADY_COVERED: 'A vehicle already has an active membership.',
  ALREADY_IN_FAMILY: 'A vehicle is already part of a Family Plan.',
  PLATE_CHECKOUT_PENDING:
    'A payment for one of these vehicles is still in progress. Try again in a few minutes.',
  FEATURE_DISABLED: 'Family Plan is not enabled for this location.',
  PAYMENT_NOT_COMPLETE: 'The payment has not completed yet.',
  FAMILY_CHECKOUT_NOT_FOUND:
    'We could not find this payment attempt. Check the Orders page to see if it completed.',
};

export function familySaleErrorMessage(code: string | null | undefined, fallback: string): string {
  return (code && ERROR_MESSAGES[code]) || fallback;
}

/** New attempt id; the caller reuses it for every retry of one payment attempt. */
export function newFamilyRequestId(): string {
  return crypto.randomUUID();
}

/** Monthly membership packages the shop marked family eligible. */
export function eligibleFamilyPackages(
  packages: OrderRequestExpandedPackage[]
): OrderRequestExpandedPackage[] {
  return packages.filter(
    item => item.washType === MONTHLY_WASH_TYPE && item.package.family_eligible === true
  );
}

/** Family sale is offered only when the flag is on and the location has saved settings. */
export function canOfferFamilySale(
  flagOn: boolean,
  settings: { enabled: boolean; configured: boolean } | null
): boolean {
  return flagOn && !!settings && settings.enabled && settings.configured;
}

/** The main package must itself be a family eligible monthly package. */
export function isPrimaryPackageFamilyEligible(
  selected: OrderRequestExpandedPackage | null | undefined
): boolean {
  return (
    !!selected &&
    selected.washType === MONTHLY_WASH_TYPE &&
    selected.package.family_eligible === true
  );
}

/** Members the family can still take: the limit counts the primary too. */
export function remainingMemberSlots(settings: FamilyPlanSettings, memberCount: number): number {
  return Math.max(0, settings.max_vehicles - 1 - memberCount);
}

export function canAddMember(settings: FamilyPlanSettings, memberCount: number): boolean {
  return remainingMemberSlots(settings, memberCount) > 0;
}

export interface MemberPrice {
  discount: number;
  recurringPrice: number;
}

/** Preview only; the server quote is the source of truth. */
export function previewMemberPrice(
  listPrice: number,
  type: 'percentage' | 'flat',
  value: number
): MemberPrice {
  const listCents = toCents(listPrice);
  const raw = type === 'percentage' ? Math.round((listCents * value) / 100) : toCents(value);
  const discountCents = Math.min(Math.max(raw, 0), listCents);
  return {
    discount: toDollars(discountCents),
    recurringPrice: toDollars(listCents - discountCents),
  };
}

export function digitsOnly(value: string): string {
  return (value || '').replace(/\D/g, '');
}

/** "Taufik Ahamed"; either part may be missing. */
export function memberFullName(member: { firstName: string; lastName: string }): string {
  return [member.firstName, member.lastName]
    .map(part => (part || '').trim())
    .filter(Boolean)
    .join(' ');
}

export function normalizePlate(value: string): string {
  return (value || '').trim().toUpperCase().replace(/\s+/g, '');
}

/** Plates already in the family (primary plus members); the member being edited does not clash with itself. */
export function familyTakenPlates(
  primaryPlate: string,
  members: readonly Pick<FamilyMemberDraft, 'localId' | 'plate'>[],
  editingId: string | null = null
): string[] {
  return [primaryPlate, ...members.filter(m => m.localId !== editingId).map(m => m.plate)].filter(
    Boolean
  );
}

/** Name and phone are optional; when given they follow the main Order Request form's rules. */
export function validateFamilyMember(
  draft: Pick<FamilyMemberDraft, 'firstName' | 'lastName' | 'phone' | 'plate' | 'packageId'> & {
    discountValue: number | null;
  },
  takenPlates: string[],
  allowedDiscounts: readonly number[]
): FamilyMemberErrors {
  const errors: FamilyMemberErrors = {};
  const firstNameError = validateCustomerName(draft.firstName);
  if (firstNameError) {
    errors.firstName = firstNameError;
  }
  const lastNameError = validateCustomerName(draft.lastName);
  if (lastNameError) {
    errors.lastName = lastNameError;
  }
  const phoneError = validateCustomerPhone(draft.phone);
  if (phoneError) {
    errors.phone = phoneError;
  }
  const plate = normalizePlate(draft.plate);
  if (!plate) {
    errors.plate = 'Enter the license plate.';
  } else if (plate.length > MAX_PLATE_LENGTH) {
    errors.plate = `Use ${MAX_PLATE_LENGTH} characters or fewer.`;
  } else if (takenPlates.map(normalizePlate).includes(plate)) {
    errors.plate = 'This plate is already in the family.';
  }
  if (!draft.packageId) {
    errors.packageId = 'Pick a package.';
  }
  if (draft.discountValue == null || !allowedDiscounts.includes(draft.discountValue)) {
    errors.discount = 'Pick a discount from the list.';
  }
  return errors;
}

export function hasMemberErrors(errors: FamilyMemberErrors): boolean {
  return Object.keys(errors).length > 0;
}

function optionalText<K extends string>(key: K, value: string): Partial<Record<K, string>> {
  const text = (value || '').trim();
  return text ? ({ [key]: text } as Record<K, string>) : {};
}

export function toSaleMember(draft: FamilyMemberDraft): FamilySaleMember {
  return {
    plate: normalizePlate(draft.plate),
    state: draft.state,
    package_id: draft.packageId,
    discount: { type: 'preset', value: draft.discountValue },
    ...optionalText('first_name', draft.firstName),
    ...optionalText('last_name', draft.lastName),
    ...optionalText('phone', sanitizeCustomerPhoneInput(draft.phone)),
  };
}

export function buildFamilyQuotePayload(
  locationId: string,
  primary: FamilySalePrimary,
  members: FamilyMemberDraft[]
): FamilySaleQuotePayload {
  return {
    location_id: locationId,
    primary: { ...primary, plate: normalizePlate(primary.plate) },
    members: members.map(toSaleMember),
  };
}

/**
 * The scan to link to the primary's order: only when the form was opened by a lane scan. A sale
 * started from the "+" button has no scan, so nothing is linked and the primary is not redeemed.
 */
export function familyLprEntryId(
  orderSource: 'lpr' | 'saas',
  entryId: string | null | undefined
): string | undefined {
  const id = (entryId ?? '').trim();
  return orderSource === 'lpr' && id ? id : undefined;
}

/** "$47.00" */
export function formatFamilyMoney(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/** Monthly list price of a package option; 0 when missing. */
export function familyPackageListPrice(item: OrderRequestExpandedPackage | undefined): number {
  const price = Number(item?.package.monthly_list_price);
  return Number.isFinite(price) && price > 0 ? price : 0;
}

/** "Champion Monthly Wash · $50.00/mo" for the package dropdown. */
export function familyPackageLabel(item: OrderRequestExpandedPackage): string {
  return `${item.displayName} · ${formatFamilyMoney(familyPackageListPrice(item))}/mo`;
}

/** Booking-form values the primary plate is sold from. */
export interface FamilyPrimarySource {
  customerId: string;
  plate: string;
  state: string;
  packageId: string;
  addOns: CreateOrderAddOnVariantPayload[];
}

/** The primary pays the full package price: no discount fields are ever sent for it. */
export function buildFamilyPrimary(source: FamilyPrimarySource): FamilySalePrimary {
  const primary: FamilySalePrimary = {
    customer_id: source.customerId,
    plate: normalizePlate(source.plate),
    state: source.state,
    package_id: source.packageId,
  };
  if (source.addOns.length) {
    primary.add_on_variants = source.addOns;
  }
  return primary;
}

export interface FamilyCheckoutRow {
  role: 'primary' | 'member';
  plate: string;
  packageName: string;
  customerName: string;
  /** Monthly price after the family discount. */
  price: number;
  /** Shown struck through when a discount applies. */
  listPrice: number;
  discounted: boolean;
}

/** One row per plate: quote line 0 is the primary, then the members in the order they were sent. */
export function familyCheckoutRows(
  quote: FamilySaleQuote,
  primary: { plate: string; packageName: string; customerName: string },
  members: FamilyMemberDraft[],
  packages: OrderRequestExpandedPackage[]
): FamilyCheckoutRow[] {
  return quote.lines.map((line, index) => {
    const member = index === 0 ? null : members[index - 1];
    const packageName = member
      ? (packages.find(item => item.package.id === member.packageId)?.displayName ?? '')
      : primary.packageName;
    return {
      role: line.role,
      plate: member ? member.plate : normalizePlate(primary.plate),
      packageName,
      customerName: member ? memberFullName(member) : primary.customerName,
      price: line.recurringPrice,
      listPrice: line.listPrice,
      discounted: line.recurringDiscount > 0,
    };
  });
}

export interface FamilyCheckoutBreakdown {
  subtotal: number;
  addon: number;
  discount: number;
  total: number;
}

/** Summary rows from the server quote; the client never prices the sale itself. */
export function familyCheckoutBreakdown(quote: FamilySaleQuote): FamilyCheckoutBreakdown {
  const sumCents = (pick: (line: FamilySaleQuote['lines'][number]) => number): number =>
    quote.lines.reduce((sum, line) => sum + toCents(pick(line)), 0);
  return {
    subtotal: toDollars(sumCents(line => line.listPrice)),
    addon: quote.addOnTotal,
    discount: toDollars(sumCents(line => line.recurringDiscount + line.firstTimeDiscount)),
    total: quote.total,
  };
}

/**
 * Message for a sale error, naming the plates the server lists in `details` (strings or
 * `{plate_key, package_name}`). A plate listed more than once shows once, with each of its plans:
 * "ABC1 (Gold Monthly, Silver Monthly)". The state prefix of the plate key ("CA:") is dropped.
 */
export function familyErrorWithPlates(
  code: string | null | undefined,
  details: unknown,
  fallback: string
): string {
  const message = familySaleErrorMessage(code, fallback);
  const rows: unknown[] = Array.isArray(details) ? details : [];
  const packagesByPlate = new Map<string, Set<string>>();
  for (const item of rows) {
    const row = typeof item === 'string' ? { plate_key: item } : (item as FamilyErrorDetail | null);
    const plate = (row?.plate_key || '').split(':').pop();
    if (!plate) continue;
    const packages = packagesByPlate.get(plate) ?? new Set<string>();
    if (row?.package_name) packages.add(row.package_name);
    packagesByPlate.set(plate, packages);
  }
  const plates = [...packagesByPlate].map(([plate, packages]) =>
    packages.size ? `${plate} (${[...packages].join(', ')})` : plate
  );
  return plates.length ? `${message} ${plates.join(', ')}` : message;
}

/** Complete can be retried while the payment is still settling. */
export function isRetryableFamilyCompleteCode(code: string | null | undefined): boolean {
  return code === 'PAYMENT_NOT_COMPLETE' || code === 'CHECKOUT_IN_PROGRESS';
}
